import {
    DEFAULT_CAMPAIGN_DEADLINES,
    DEFAULT_CAMPAIGN_PLATFORMS,
    DEFAULT_CREATOR_PAYOUT_FEE,
    DEFAULT_INFLUENCER_TIERS,
    DEFAULT_UGC_BUDGET,
} from '../constants/ugcCampaignConfigDefaults';
import type {
    ICampaignDeadlinesConfig,
    ICampaignPlatformConfig,
    ICreatorPayoutFeeConfig,
    IInfluencerTierConfig,
    IUgcBudgetConfig,
    IUgcCampaignFeeConfig,
} from '../types/global/ugcCampaignConfig.types';

/** Reads subscribed min payout; accepts legacy wrong field name from early client builds. */
function readSubscribedMinPayout(
    tier: IInfluencerTierConfig & { platform_fee_per_platform_usd_subscribed?: number },
): number | undefined {
    if (typeof tier.min_payout_per_creator_usd_subscribed === 'number') {
        return tier.min_payout_per_creator_usd_subscribed;
    }
    if (typeof tier.platform_fee_per_platform_usd_subscribed === 'number') {
        return tier.platform_fee_per_platform_usd_subscribed;
    }
    return undefined;
}

export function normalizeInfluencerTiers(tiers?: IInfluencerTierConfig[] | null): IInfluencerTierConfig[] {
    if (!tiers?.length) return DEFAULT_INFLUENCER_TIERS;
    return tiers.map((tier, index) => {
        const fallback = DEFAULT_INFLUENCER_TIERS.find(d => d.id === tier.id);
        let max = tier.max_followers !== undefined ? tier.max_followers : fallback?.max_followers ?? null;
        if (max === ('' as unknown as number)) max = null;
        if (max != null) {
            const n = Math.round(Number(max));
            max = Number.isFinite(n) ? n : null;
        }
        return {
            ...fallback,
            ...tier,
            min_followers: Math.max(0, Math.round(Number(tier.min_followers ?? fallback?.min_followers) || 0)),
            max_followers: max,
            min_payout_per_creator_usd: tier.min_payout_per_creator_usd ?? fallback?.min_payout_per_creator_usd ?? 15,
            platform_fee_per_platform_usd:
                tier.platform_fee_per_platform_usd ?? fallback?.platform_fee_per_platform_usd ?? 15,
            min_payout_per_creator_usd_subscribed: readSubscribedMinPayout(tier),
            sort_order: tier.sort_order ?? index,
            active: tier.active !== false,
        };
    });
}

export function getActiveInfluencerTiers(tiers?: IInfluencerTierConfig[] | null): IInfluencerTierConfig[] {
    return normalizeInfluencerTiers(tiers)
        .filter(t => t.active !== false)
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

export function normalizeCampaignPlatforms(platforms?: ICampaignPlatformConfig[] | null): ICampaignPlatformConfig[] {
    if (!platforms?.length) return DEFAULT_CAMPAIGN_PLATFORMS;
    return platforms;
}

/** Platforms shown in the wizard (hide only when disabled and not coming soon). */
export function getVisibleCampaignPlatforms(platforms?: ICampaignPlatformConfig[] | null): ICampaignPlatformConfig[] {
    return normalizeCampaignPlatforms(platforms).filter(p => p.enabled || p.coming_soon);
}

export function normalizeUgcBudget(budget?: IUgcBudgetConfig | null): IUgcBudgetConfig {
    if (!budget) return DEFAULT_UGC_BUDGET;
    return {
        absolute_min_total_budget_usd:
            budget.absolute_min_total_budget_usd ?? DEFAULT_UGC_BUDGET.absolute_min_total_budget_usd,
        default_min_participants: budget.default_min_participants ?? DEFAULT_UGC_BUDGET.default_min_participants,
        suggested_budget_multiplier_comfort:
            budget.suggested_budget_multiplier_comfort ?? DEFAULT_UGC_BUDGET.suggested_budget_multiplier_comfort,
        suggested_budget_multiplier_strong:
            budget.suggested_budget_multiplier_strong ?? DEFAULT_UGC_BUDGET.suggested_budget_multiplier_strong,
    };
}

export function normalizeCreatorPayoutFee(fee?: ICreatorPayoutFeeConfig | null): ICreatorPayoutFeeConfig {
    if (!fee) return DEFAULT_CREATOR_PAYOUT_FEE;
    return {
        platform_fee_rate:
            typeof fee.platform_fee_rate === 'number'
                ? fee.platform_fee_rate
                : DEFAULT_CREATOR_PAYOUT_FEE.platform_fee_rate,
        platform_fee_rate_subscriber:
            typeof fee.platform_fee_rate_subscriber === 'number'
                ? fee.platform_fee_rate_subscriber
                : DEFAULT_CREATOR_PAYOUT_FEE.platform_fee_rate_subscriber,
        waive_fee_when_host_referred:
            fee.waive_fee_when_host_referred === 'active' || fee.waive_fee_when_host_referred === 'inactive'
                ? fee.waive_fee_when_host_referred
                : DEFAULT_CREATOR_PAYOUT_FEE.waive_fee_when_host_referred,
    };
}

function clampDeadlineDays(value: unknown, fallback: number): number {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 1) return fallback;
    return Math.min(90, Math.floor(n));
}

export function normalizeCampaignDeadlines(
    deadlines?: Partial<ICampaignDeadlinesConfig> | null,
): ICampaignDeadlinesConfig {
    if (!deadlines)
        return {
            ...DEFAULT_CAMPAIGN_DEADLINES,
            pre_end_reminder_days: [...DEFAULT_CAMPAIGN_DEADLINES.pre_end_reminder_days],
        };
    const preEnd = Array.isArray(deadlines.pre_end_reminder_days)
        ? deadlines.pre_end_reminder_days
              .map(d => Number(d))
              .filter(d => Number.isFinite(d) && d > 0)
              .map(d => Math.min(90, Math.floor(d)))
        : DEFAULT_CAMPAIGN_DEADLINES.pre_end_reminder_days;
    return {
        auto_approve_days: clampDeadlineDays(deadlines.auto_approve_days, DEFAULT_CAMPAIGN_DEADLINES.auto_approve_days),
        mark_complete_deadline_days: clampDeadlineDays(
            deadlines.mark_complete_deadline_days,
            DEFAULT_CAMPAIGN_DEADLINES.mark_complete_deadline_days,
        ),
        pre_end_reminder_days: preEnd.length ? preEnd : [...DEFAULT_CAMPAIGN_DEADLINES.pre_end_reminder_days],
    };
}

export function getEffectiveMinPayoutPerCreator(
    tier: IInfluencerTierConfig,
    hostHasActiveSubscription?: boolean,
): number {
    const subscribed = tier.min_payout_per_creator_usd_subscribed;
    if (hostHasActiveSubscription && typeof subscribed === 'number' && subscribed >= 0) {
        return subscribed;
    }
    return tier.min_payout_per_creator_usd;
}

export function buildUgcCampaignFeeConfig(
    tiers?: IInfluencerTierConfig[] | null,
    ugcBudget?: IUgcBudgetConfig | null,
    creatorPayout?: ICreatorPayoutFeeConfig | null,
): IUgcCampaignFeeConfig {
    return {
        tiers: getActiveInfluencerTiers(tiers),
        ugcBudget: normalizeUgcBudget(ugcBudget),
        creatorPayout: normalizeCreatorPayoutFee(creatorPayout),
    };
}

export function findTierForBreakdown(tiers: IInfluencerTierConfig[], tierId: string): IInfluencerTierConfig {
    const match = tiers.find(t => t.id === tierId && t.active !== false);
    return match ?? tiers[0] ?? DEFAULT_INFLUENCER_TIERS[0];
}

/** 300 → "300", 50000 → "50K", 1000000 → "1M" */
export function formatFollowerThreshold(count: number): string {
    const n = Number(count) || 0;
    if (n >= 1_000_000) {
        const m = n / 1_000_000;
        return Number.isInteger(m) ? `${m}M` : `${parseFloat(m.toFixed(1))}M`;
    }
    if (n >= 1_000) {
        const k = n / 1_000;
        return Number.isInteger(k) ? `${k}K` : `${parseFloat(k.toFixed(1))}K`;
    }
    return String(n);
}

function formatPlatformList(platforms: string[]): string {
    const labels = platforms.map(p => {
        const id = String(p).toLowerCase();
        if (id === 'instagram') return 'Instagram';
        if (id === 'tiktok') return 'TikTok';
        if (id === 'youtube') return 'YouTube';
        if (id === 'facebook') return 'Facebook';
        if (id === 'x' || id === 'twitter') return 'X';
        return id.charAt(0).toUpperCase() + id.slice(1);
    });
    if (!labels.length) return 'your connected platforms';
    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

const TIER_FOLLOWER_PLATFORM_KEYS = ['instagram', 'tiktok', 'youtube', 'facebook', 'twitter', 'thspian'] as const;

/**
 * Tier follower gate for apply:
 * - 1 requested platform → that platform must meet the follower band
 * - 2+ requested platforms → at least one must meet the band
 * - no platforms listed → any known profile platform may satisfy
 * Band: followers >= min AND (max == null OR followers <= max)
 */
export function creatorMeetsCampaignTierFollowerMinimum(options: {
    minFollowers: number;
    maxFollowers?: number | null;
    requiredPlatforms?: string[] | null;
    getFollowers: (platform: string) => number | null | undefined;
}): boolean {
    const min = Math.max(0, Number(options.minFollowers) || 0);
    let max: number | null =
        options.maxFollowers === undefined || options.maxFollowers === null ? null : Number(options.maxFollowers);
    if (max != null && !Number.isFinite(max)) max = null;

    const requested = (options.requiredPlatforms || [])
        .map(p =>
            String(p || '')
                .toLowerCase()
                .trim(),
        )
        .filter(Boolean);
    const keys = requested.length
        ? requested.filter(k => (TIER_FOLLOWER_PLATFORM_KEYS as readonly string[]).includes(k) || k === 'x')
        : [...TIER_FOLLOWER_PLATFORM_KEYS];

    if (!keys.length) return true;

    return keys.some(platform => {
        const key = platform === 'x' ? 'twitter' : platform;
        const followers = Number(options.getFollowers(key) ?? options.getFollowers(platform));
        if (!Number.isFinite(followers) || followers < min) return false;
        if (max != null && followers > max) return false;
        return true;
    });
}

export interface ICampaignCreatorRequirementSummary {
    tierId: string;
    tierName: string;
    reach: string;
    minFollowers: number;
    /** Short lines for chips / checklist */
    lines: string[];
}

/**
 * Human-readable creator eligibility for a campaign (tier + min followers on target platforms).
 */
export function resolveCampaignCreatorRequirements(
    campaign:
        | {
              influencer_tier?: string | null;
              platforms?: string[] | null;
          }
        | null
        | undefined,
    tiers?: IInfluencerTierConfig[] | null,
): ICampaignCreatorRequirementSummary | null {
    if (!campaign?.influencer_tier) return null;
    const catalog = normalizeInfluencerTiers(tiers);
    const tier = findTierForBreakdown(catalog, String(campaign.influencer_tier));
    if (!tier) return null;

    const minFollowers = Number(tier.min_followers) || 0;
    const minLabel = formatFollowerThreshold(minFollowers);
    const platforms = (campaign.platforms || []).map(p => String(p).toLowerCase()).filter(Boolean);
    const platformText = formatPlatformList(platforms);
    const tierTitle = tier.name?.toLowerCase().includes('influencer') ? tier.name : `${tier.name} influencer`;

    const followerLine =
        platforms.length <= 1
            ? `Minimum ${minLabel} followers on ${platformText}`
            : `Minimum ${minLabel} followers on at least one of ${platformText}`;

    const lines = [`${tierTitle} (${tier.reach || `${minLabel}+`})`, followerLine];

    return {
        tierId: tier.id,
        tierName: tier.name,
        reach: tier.reach,
        minFollowers,
        lines,
    };
}

export function isPlatformSelectable(platform: ICampaignPlatformConfig): boolean {
    return platform.enabled === true;
}

export function isPlatformComingSoon(platform: ICampaignPlatformConfig): boolean {
    return platform.enabled === false && platform.coming_soon === true;
}

/** Only count enabled platforms toward fee breakdown. */
export function filterEnabledSelectedPlatforms(selectedIds: string[], platforms: ICampaignPlatformConfig[]): string[] {
    const enabledIds = new Set(platforms.filter(isPlatformSelectable).map(p => p.id));
    return (selectedIds || []).filter(id => enabledIds.has(id));
}
